import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import LandForm from "@/components/site/LandForm";
import { Eyebrow, Reveal } from "@/components/site/Bits";
import { SELL_LAND } from "@/lib/sellLandCopy";

const SITE = "https://verde2050.com";

function LangSwitch({ lang }) {
    return (
        <div className="inline-flex rounded-full border border-cream/30 p-1 text-xs font-mono uppercase tracking-[0.08em]" data-testid="sell-land-lang-switch">
            {["en", "pt"].map((l) => (
                <Link key={l} to={SELL_LAND[l].path} data-testid={`sell-land-lang-${l}`} aria-current={lang === l ? "page" : undefined}
                      className={`rounded-full px-4 py-1.5 transition-colors ${lang === l ? "bg-cream text-forest" : "text-cream/70 hover:text-cream"}`}>
                    {l}
                </Link>
            ))}
        </div>
    );
}

function Process({ t }) {
    return (
        <section data-testid="sell-land-process" className="bg-cream py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal><Eyebrow>{t.processLabel}</Eyebrow></Reveal>
                <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight mt-8 mb-14">{t.processTitle}</h2></Reveal>
                <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-hairline rounded-2xl overflow-hidden">
                    {t.steps.map(([title, body], i) => (
                        <Reveal as="li" key={title} delay={i * 90} className="bg-cream p-7 md:p-8 min-h-[240px] flex flex-col" data-testid={`sell-land-step-${i + 1}`}>
                            <span className="font-serif text-6xl text-sage leading-none mb-8">{String(i + 1).padStart(2, "0")}</span>
                            <p className="font-serif text-2xl text-forest leading-tight mb-3">{title}</p>
                            <p className="text-sm text-muted_text leading-relaxed">{body}</p>
                        </Reveal>
                    ))}
                </ol>
            </div>
        </section>
    );
}

export default function SellLand({ lang = "en" }) {
    const t = SELL_LAND[lang];
    const scrollToForm = (e) => { e.preventDefault(); (window.innerWidth < 1024 ? document.querySelector('[data-testid="land-form"]') : document.getElementById("submit-land"))?.scrollIntoView({ behavior: "smooth", block: "start" }); };

    return (
        <div data-testid="sell-land-page" className="bg-cream text-ink min-h-screen">
            <SEO path={t.path} og={lang === "pt" ? "vender-terreno" : "sell-land"} title={t.seoTitle} description={t.seoDescription} />
            <Helmet htmlAttributes={{ lang: lang === "pt" ? "pt-PT" : "en" }}>
                <link rel="alternate" hrefLang="en" href={`${SITE}${SELL_LAND.en.path}`} />
                <link rel="alternate" hrefLang="pt-PT" href={`${SITE}${SELL_LAND.pt.path}`} />
                <link rel="alternate" hrefLang="x-default" href={`${SITE}${SELL_LAND.en.path}`} />
            </Helmet>
            <GroupHeader />

            <section className="relative isolate overflow-hidden bg-forest text-cream pt-32 md:pt-44 pb-16 md:pb-28">
                <img src="/v2050-walk_land.jpg" alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover hero-zoom" loading="eager" />
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-forest via-forest/80 to-forest/30" />
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex flex-wrap items-center justify-between gap-6">
                        <Reveal><Eyebrow dark>{t.eyebrow}</Eyebrow></Reveal>
                        <LangSwitch lang={lang} />
                    </div>
                    <Reveal delay={120}>
                        <h1 data-testid="sell-land-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-10 max-w-4xl">
                            {t.title}<br /><em className="italic text-sage">{t.titleEm}</em>
                        </h1>
                    </Reveal>
                    <Reveal delay={240}><p className="mt-8 max-w-2xl text-base md:text-lg text-cream/85 leading-relaxed">{t.sub}</p></Reveal>
                    <Reveal delay={320} className="mt-10">
                        <a href="#submit-land" onClick={scrollToForm} data-testid="sell-land-hero-cta"
                           className="inline-flex items-center gap-3 rounded-full bg-cream text-forest hover:bg-sage px-7 py-4 text-sm font-medium transition-colors">{t.heroCta}</a>
                    </Reveal>
                </div>
            </section>

            <Process t={t} />

            <section id="submit-land" data-testid="sell-land-form-section" className="bg-cream pb-24 md:pb-32 scroll-mt-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <Reveal><Eyebrow>{t.formLabel}</Eyebrow></Reveal>
                        <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight mt-8">{t.formTitle}</h2></Reveal>
                        <Reveal delay={150}><p className="mt-6 text-base text-muted_text leading-relaxed">{t.formIntro}</p></Reveal>
                        <Reveal delay={200} className="mt-10">
                            <p className="mono-label text-moss mb-4">{t.lookLabel}</p>
                            <ul className="space-y-3">
                                {t.look.map((l) => <li key={l} className="flex gap-3 text-sm text-ink leading-relaxed"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-moss" />{l}</li>)}
                            </ul>
                        </Reveal>
                        <Reveal delay={250} className="mt-10 rounded-2xl border border-hairline p-6" data-testid="sell-land-trust">
                            <p className="flex items-center gap-2 font-medium text-forest mb-4"><ShieldCheck className="h-5 w-5 text-moss" /> {t.trustTitle}</p>
                            <ul className="space-y-2.5">
                                {t.trust.map((l) => <li key={l} className="text-sm text-muted_text leading-relaxed">— {l}</li>)}
                            </ul>
                        </Reveal>
                    </div>
                    <Reveal delay={150} className="lg:col-span-8">
                        <LandForm t={t} lang={lang} />
                    </Reveal>
                </div>
            </section>
            <GroupFooter />
        </div>
    );
}
